import { useEffect, useRef } from "react";
import { useLocation, useSearch } from "wouter";
import authStore from "../stores/auth/authStore";
import { sendPageViewEvent } from "./eventTelemetry";

/**
 * Sends a PAGE_VIEW telemetry event on navigation, once the user is authenticated.
 *
 * A restored session isn't verified yet on first mount, so we also re-run when auth
 * becomes ready, and de-dupe on the reported location (path + query string) so the
 * same page isn't reported twice. The query string comes from wouter's `useSearch`
 * (a reactive subscription to location.search) so a query-only navigation — which
 * `useLocation` alone would not re-render on — still reports a fresh page view. The
 * ref is cleared on logout so a re-login on the same route reports a fresh page view.
 *
 * Must be called from an `observer` component so the isLoggedIn read stays reactive.
 */
export const usePageViewTracking = () => {
   const [location] = useLocation();
   const search = useSearch();
   const isLoggedIn = authStore.isLoggedIn();
   const lastPageViewLocationRef = useRef<string | null>(null);

   useEffect(() => {
      if (!isLoggedIn) {
         lastPageViewLocationRef.current = null;
         return;
      }
      const currentLocation = search ? `${location}?${search}` : location;
      if (lastPageViewLocationRef.current === currentLocation) return;
      lastPageViewLocationRef.current = currentLocation;
      sendPageViewEvent();
   }, [location, search, isLoggedIn]);
};
